import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Search, MapPin, ArrowRight, Sparkles } from "lucide-react";
import { get } from "@/lib/api";
import { CATEGORY_ICONS } from "@/lib/data";
import { StudioCard } from "@/components/consumer/StudioCard";
import { SessionCard } from "@/components/consumer/SessionCard";
import { useNavigate } from "react-router-dom";

const REGIONS = ["Kadıköy", "Caddebostan", "Suadiye", "Erenköy", "Ataşehir", "Beşiktaş", "Nişantaşı"];

function SectionHeader({ title, subtitle, to }) {
  return (
    <div className="mb-7 flex items-end justify-between">
      <div>
        <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h2>
        {subtitle && <p className="mt-1 text-[#5C5856]">{subtitle}</p>}
      </div>
      {to && (
        <Link to={to} className="hidden items-center gap-1 text-sm font-medium text-[#8C6A5D] hover:gap-2 sm:flex">
          Tümünü gör <ArrowRight className="h-4 w-4 transition-all" />
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [region, setRegion] = useState("Kadıköy");
  const [q, setQ] = useState("");
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: () => get("/categories") });
  const { data: home } = useQuery({ queryKey: ["home"], queryFn: () => get("/home") });

  const submitSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("search", q);
    if (region) params.set("region", region);
    navigate(`/kesfet?${params.toString()}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1761971975962-9cc397e2ba2a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt=""
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2D2926]/85 via-[#2D2926]/50 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-36">
          <div className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-sm text-white backdrop-blur">
              <Sparkles className="h-4 w-4" /> Tek üyelik, sınırsız wellness
            </span>
            <h1 className="mt-5 font-display text-5xl font-semibold leading-[1.05] text-white sm:text-6xl lg:text-7xl">
              Şehrin en iyi stüdyolarını keşfet
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/80">
              Pilates, yoga, fitness, spa ve daha fazlası. Kredinle dilediğin
              stüdyoda, dilediğin seansa anında rezervasyon yap.
            </p>

            <form
              onSubmit={submitSearch}
              className="mt-8 flex flex-col gap-3 rounded-2xl bg-white/95 p-3 shadow-2xl backdrop-blur sm:flex-row sm:items-center"
            >
              <div className="flex flex-1 items-center gap-2 px-3">
                <Search className="h-5 w-5 text-[#8C6A5D]" />
                <input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Stüdyo veya aktivite ara"
                  className="w-full bg-transparent py-2.5 text-[#2D2926] outline-none placeholder:text-[#9C948B]"
                />
              </div>
              <div className="flex items-center gap-2 border-t border-[#E5E0D8] px-3 sm:border-l sm:border-t-0">
                <MapPin className="h-5 w-5 text-[#8C6A5D]" />
                <select
                  data-testid="hero-region-select"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  className="bg-transparent py-2.5 text-[#2D2926] outline-none"
                >
                  {REGIONS.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <button
                data-testid="hero-search-button"
                type="submit"
                className="rounded-xl bg-[#8C6A5D] px-7 py-3 font-medium text-white transition-colors hover:bg-[#705448]"
              >
                Ara
              </button>
            </form>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        {/* Categories */}
        <section className="py-16">
          <SectionHeader title="Kategoriler" subtitle="İlgini çeken alanı seç" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-8">
            {categories.map((c, i) => {
              const Icon = CATEGORY_ICONS[c.slug];
              return (
                <Link
                  key={c.slug}
                  to={`/kesfet?category=${c.slug}`}
                  data-testid={`category-card-${c.slug}`}
                  className="group flex animate-fade-up flex-col items-center gap-3 rounded-2xl border border-[#E5E0D8] bg-white p-5 text-center transition-all hover:border-[#8C6A5D] hover:bg-[#F4EFE7]"
                  style={{ animationDelay: `${i * 40}ms` }}
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#E8DED1] text-[#8C6A5D] transition-colors group-hover:bg-[#8C6A5D] group-hover:text-white">
                    {Icon && <Icon className="h-6 w-6" />}
                  </div>
                  <span className="text-sm font-medium">{c.name}</span>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Today's sessions */}
        <section className="py-8">
          <SectionHeader title="Bugün müsait seanslar" subtitle="Hemen katılabileceğin dersler" to="/kesfet" />
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {(home?.today_sessions || []).slice(0, 6).map((s, i) => (
              <SessionCard key={s.id} session={s} index={i} />
            ))}
          </div>
        </section>

        {/* Last minute */}
        {home?.last_minute?.length > 0 && (
          <section className="my-10 rounded-3xl bg-[#4A5D4E] px-6 py-12 lg:px-12">
            <div className="mb-7">
              <h2 className="font-display text-3xl font-semibold text-white sm:text-4xl">Son dakika fırsatları</h2>
              <p className="mt-1 text-white/70">İndirimli kredilerle kaçırılmayacak seanslar</p>
            </div>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {home.last_minute.slice(0, 3).map((s, i) => (
                <SessionCard key={s.id} session={s} index={i} />
              ))}
            </div>
          </section>
        )}

        {/* Popular */}
        <section className="py-8">
          <SectionHeader title="Popüler stüdyolar" subtitle="En çok tercih edilen mekanlar" to="/kesfet" />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {(home?.popular_studios || []).map((s, i) => (
              <StudioCard key={s.id} studio={s} index={i} />
            ))}
          </div>
        </section>

        {/* Nearby */}
        <section className="py-8 pb-16">
          <SectionHeader title="Yakındaki mekanlar" subtitle="Sana en yakın stüdyolar" to="/kesfet" />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {(home?.nearby_studios || []).slice(0, 3).map((s, i) => (
              <StudioCard key={s.id} studio={s} index={i} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
